import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ContactoApiService } from '../services/contacto-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ContactoDetailModalComponent } from '../contacto-detail-modal/contacto-detail-modal.component';
import { DealDrawerService } from '../../deals/services/deal-drawer.service';
import { LeadDrawerService } from '../../leads/services/lead-drawer.service';
import { ContactCompanyApiService } from '../../empresas/services/contact-company-api.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import {
  Contact,
  ContactRequest,
  ContactState,
  ContactCycle,
  ContactOrigin,
  ContactAttributeDefinition
} from '../models/contacto.model';
import { countryFromPhone, COUNTRIES_DATA } from '../../../shared/data/countries.data';
import { CountrySelectComponent } from '../../../shared/components/form/country-select/country-select.component';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

type ViewMode = 'stateLead' | 'contactCycle' | 'origin';

interface KanbanColumn {
  id: number;
  name: string;
  color: string;
  contacts: Contact[];
}

interface FilterState {
  searchTerm: string;
  idContactState?: number;
  idContactCycle?: number;
  idOrigin?: number;
  phonePrefix?: string;
}

@Component({
  selector: 'app-contacto-kanban',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    BadgeComponent,
    ContactoDetailModalComponent,
    CountrySelectComponent
  ],
  templateUrl: './contacto-kanban.component.html',
  styleUrl: './contacto-kanban.component.css',
})
export class ContactoKanbanComponent implements OnInit, OnDestroy {
  viewMode: ViewMode = 'contactCycle';
  columns: KanbanColumn[] = [];
  loading = false;
  error = '';

  // Catálogos
  contactStates: ContactState[] = [];
  contactCycles: ContactCycle[] = [];
  contactOrigins: ContactOrigin[] = [];

  // Atributos personalizados (EAV)
  attributeDefinitions: ContactAttributeDefinition[] = [];
  filtroAttributeDefinitionId: number | undefined;
  filtroAttrValue = '';

  // Filtro de responsable
  private readonly OWNER_FILTER_KEY = 'crm_owner_filter_contactos';
  /**
   * inc38 — 'unassigned' es la bandeja de contactos sin responsable. Los que entran por
   * formulario, widget de chat o página de reserva pueden quedarse sin dueño a propósito
   * (ver V179); sin este filtro no aparecían bajo "Mis contactos" de nadie y solo se veían
   * perdidos entre "Todos".
   */
  filtroOwner: 'all' | 'mine' | 'unassigned' =
    (['mine', 'unassigned'].includes(localStorage.getItem(this.OWNER_FILTER_KEY) ?? '')
      ? localStorage.getItem(this.OWNER_FILTER_KEY) as 'mine' | 'unassigned'
      : 'all');

  // Filtros
  filters: FilterState = {
    searchTerm: '',
    idContactState: undefined,
    idContactCycle: undefined,
    idOrigin: undefined
  };
  showFilters = false;

  // Modal de detalle
  showDetailModal = false;
  selectedContact: Contact | null = null;

  // Trigger drawers (CREATE_DEAL / CREATE_LEAD on cycle change)
  private triggerContact: Contact | null = null;
  private triggerTargetColumn: KanbanColumn | null = null;
  private triggerType: 'CREATE_DEAL' | 'CREATE_LEAD' | null = null;
  private readonly destroy$ = new Subject<void>();

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'contacto-kanban-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  /** Código ISO (minúscula) del país derivado del teléfono, para la bandera flag-icons. Null si no aplica. */
  countryFlagCode(phone?: string | null): string | null {
    return countryFromPhone(phone)?.code.toLowerCase() ?? null;
  }

  /** Nombre del país derivado del teléfono (para tooltip). */
  countryName(phone?: string | null): string {
    return countryFromPhone(phone)?.name ?? '';
  }

  /** Catálogo para el filtro de país (bandera + código). */
  readonly filterCountries = COUNTRIES_DATA;
  /** Código ISO del país seleccionado en el filtro ('' = todos). */
  filtroPais = '';

  /** Traduce el país elegido en el filtro a su prefijo telefónico y recarga. */
  onCountryFilterChange(code: string): void {
    this.filtroPais = code;
    this.filters.phonePrefix = code
      ? COUNTRIES_DATA.find(c => c.code === code)?.dialCode || undefined
      : undefined;
    this.applyFilters();
  }

  constructor(
    private contactoApi: ContactoApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private dealDrawerService: DealDrawerService,
    private leadDrawerService: LeadDrawerService,
    private contactCompanyApi: ContactCompanyApiService,
    private empresaApi: EmpresaApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.loadCatalogs();

    // Al llegar con ?openContact=<id> (buscador global del header, enlaces desde
    // otros módulos) se abre su modal de detalle sobre el kanban. El detalle de un
    // contacto es un modal, no una ruta propia — mismo patrón que ?openDeal= en
    // negocios, ?openLead= en leads y ?openTicket= en tickets.
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const raw = params.get('openContact');
        const id = raw ? Number(raw) : NaN;
        if (!Number.isNaN(id) && id > 0) this.abrirDetallePorId(id);
      });

    // CREATE_DEAL: deal saved → change cycle
    this.dealDrawerService.dealSaved$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.triggerType === 'CREATE_DEAL' && this.triggerContact?.id && this.triggerTargetColumn) {
        this.applyCycleChange();
      }
    });

    // CREATE_LEAD: lead saved → change cycle
    this.leadDrawerService.leadSaved$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.triggerType === 'CREATE_LEAD' && this.triggerContact?.id && this.triggerTargetColumn) {
        this.applyCycleChange();
      }
    });

    // Revert kanban if either drawer is closed without saving
    this.dealDrawerService.state.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (!state.isOpen && this.triggerType === 'CREATE_DEAL' && this.triggerContact) {
        setTimeout(() => {
          if (this.triggerContact && this.triggerType === 'CREATE_DEAL') {
            this.resetTrigger();
            this.loadPage(this.globalPage);
          }
        }, 100);
      }
    });
    this.leadDrawerService.state.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (!state.isOpen && this.triggerType === 'CREATE_LEAD' && this.triggerContact) {
        setTimeout(() => {
          if (this.triggerContact && this.triggerType === 'CREATE_LEAD') {
            this.resetTrigger();
            this.loadPage(this.globalPage);
          }
        }, 100);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyCycleChange(): void {
    const contactId = this.triggerContact!.id!;
    const targetId = this.triggerTargetColumn!.id;
    this.contactoApi.changeContactCycle(contactId, targetId).subscribe({
      next: () => {
        this.resetTrigger();
        this.loadPage(this.globalPage);
      },
      error: (err) => {
        console.error('Error al cambiar ciclo después del trigger:', err);
        this.confirmDialog.alert({ title: 'Error', message: 'El registro se creó, pero hubo un error al cambiar el ciclo del contacto.', variant: 'danger' });
        this.resetTrigger();
        this.loadPage(this.globalPage);
      }
    });
  }

  private resetTrigger(): void {
    this.triggerContact = null;
    this.triggerTargetColumn = null;
    this.triggerType = null;
  }

  private openTriggerDrawer(contact: Contact, type: 'CREATE_DEAL' | 'CREATE_LEAD'): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    const contactName = this.getFullName(contact);

    const openWith = (companyId?: number, companyName?: string) => {
      const opts = { contactId: contact.id, contactName, companyId, companyName };
      if (type === 'CREATE_DEAL') this.dealDrawerService.open(opts);
      else this.leadDrawerService.open(opts);
    };

    this.contactCompanyApi.getByContact(contact.id!, businessId).subscribe({
      next: (associations) => {
        const primary = associations.find(a => a.isPrimary) || associations[0];
        if (!primary) { openWith(); return; }
        const companyId = primary.idCompany ?? primary.company?.id;
        const name = primary.company?.commercialName;
        if (name) { openWith(companyId, name); return; }
        // Fallback: fetch company details if not embedded
        if (companyId) {
          this.empresaApi.getCompanyById(companyId).subscribe({
            next: (c) => openWith(companyId, c?.commercialName),
            error: () => openWith(companyId)
          });
        } else {
          openWith();
        }
      },
      error: () => openWith()
    });
  }

  private loadCatalogs(): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.contactoApi.getAttributeDefinitions(businessId).subscribe({
      next: (data) => this.attributeDefinitions = data.filter(d => d.isActive),
      error: () => { }
    });

    Promise.all([
      this.contactoApi.getContactStates(businessId).toPromise(),
      this.contactoApi.getContactCycles(businessId).toPromise(),
      this.contactoApi.getContactOrigins(businessId).toPromise()
    ]).then(([contactStates, contactCycles, contactOrigins]) => {
      this.contactStates = contactStates || [];
      this.contactCycles = contactCycles || [];
      this.contactOrigins = contactOrigins || [];
      this.initializeColumns();
      this.loading = false;
    }).catch(err => {
      this.error = 'Error al cargar catálogos';
      this.loading = false;
      console.error('Error:', err);
    });
  }

  private initializeColumns(): void {
    const colors = ['bg-blue-500','bg-green-500','bg-orange-500','bg-purple-500','bg-pink-500','bg-indigo-500','bg-yellow-500','bg-red-500'];

    switch (this.viewMode) {
      case 'stateLead':
        this.columns = [
          ...this.contactStates.map((s, i) => ({ id: s.id, name: s.name, color: colors[i % colors.length], contacts: [] })),
          { id: 0, name: 'Sin Estado', color: 'bg-gray-500', contacts: [] }
        ];
        break;
      case 'contactCycle':
        this.columns = [
          ...this.contactCycles.map((c, i) => ({ id: c.id, name: c.name, color: colors[i % colors.length], contacts: [] })),
          { id: 0, name: 'Sin Ciclo', color: 'bg-gray-500', contacts: [] }
        ];
        break;
      case 'origin':
        this.columns = [
          ...this.contactOrigins.map((o, i) => ({ id: o.id, name: o.name, color: colors[i % colors.length], contacts: [] })),
          { id: 0, name: 'Sin Origen', color: 'bg-gray-500', contacts: [] }
        ];
        break;
    }

    this.loadPage(0);
  }

  private loadPage(page: number): void {
    this.loading = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const params: any = {
      businessId,
      page,
      size: this.pageSize
    };

    if (this.filters.searchTerm) params.searchTerm = this.filters.searchTerm;
    if (this.filters.phonePrefix) params.phonePrefix = this.filters.phonePrefix;
    if (this.filtroOwner === 'mine') params.userPropietyId = user?.id;
    if (this.filtroOwner === 'unassigned') params.withoutOwner = true;

    // Filtros cruzados (excluye la dimensión del viewMode actual)
    if (this.viewMode !== 'stateLead'   && this.filters.idContactState  !== undefined) params.idContactState  = this.filters.idContactState;
    if (this.viewMode !== 'contactCycle' && this.filters.idContactCycle !== undefined) params.idContactCycle = this.filters.idContactCycle;
    if (this.viewMode !== 'origin'      && this.filters.idOrigin        !== undefined) params.idOrigin       = this.filters.idOrigin;

    const def = this.selectedAttrDef;
    if (def && this.filtroAttrValue !== '' && this.filtroAttrValue != null) {
      params.attributeDefinitionId = def.id;
      switch (def.type) {
        case 'text':    params.attrStrValue    = this.filtroAttrValue;             break;
        case 'list':    params.attrStrListValue = this.filtroAttrValue;            break;
        case 'number':  params.attrIntValue     = Number(this.filtroAttrValue);    break;
        case 'boolean': params.attrBolValue     = this.filtroAttrValue === 'true'; break;
      }
    }

    this.columns.forEach(col => col.contacts = []);

    this.contactoApi.searchContacts(params).subscribe({
      next: (response) => {
        response.content.forEach(contact => {
          const col = this.findColumnForContact(contact);
          if (col) col.contacts.push(contact);
        });
        this.globalPage = page;
        this.globalTotalPages = response.totalPages;
        this.globalTotalElements = response.totalElements;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error al cargar contactos:', err);
        this.error = 'Error al cargar contactos';
        this.loading = false;
      }
    });
  }

  private findColumnForContact(contact: Contact): KanbanColumn | undefined {
    switch (this.viewMode) {
      case 'stateLead':
        return this.columns.find(col =>
          col.id === 0 ? (!contact.idContactState || contact.idContactState === 0) : col.id === contact.idContactState
        );
      case 'contactCycle':
        return this.columns.find(col =>
          col.id === 0 ? (!contact.idContactCycle || contact.idContactCycle === 0) : col.id === contact.idContactCycle
        );
      case 'origin':
        return this.columns.find(col =>
          col.id === 0 ? (!contact.idOrigin || contact.idOrigin === 0) : col.id === contact.idOrigin
        );
    }
  }

  changeViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    this.initializeColumns();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  applyFilters(): void {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.loadPage(0);
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.applyFilters();
  }

  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.loadPage(page);
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }

  get hasPrevPage(): boolean {
    return this.globalPage > 0;
  }

  get hasNextPage(): boolean {
    return this.globalPage < this.globalTotalPages - 1;
  }

  get pageDisplay(): number {
    return this.globalPage + 1;
  }

  setOwnerFilter(value: 'all' | 'mine' | 'unassigned'): void {
    this.filtroOwner = value;
    localStorage.setItem(this.OWNER_FILTER_KEY, value);
    this.applyFilters();
  }

  clearFilters(): void {
    this.filtroOwner = 'all';
    this.filters = { searchTerm: '', idContactState: undefined, idContactCycle: undefined, idOrigin: undefined, phonePrefix: undefined };
    this.filtroPais = '';
    this.filtroAttributeDefinitionId = undefined;
    this.filtroAttrValue = '';
    this.applyFilters();
  }

  onAttrDefChange(): void {
    this.filtroAttrValue = '';
    this.applyFilters();
  }

  get selectedAttrDef(): ContactAttributeDefinition | undefined {
    return this.attributeDefinitions.find(d => d.id === this.filtroAttributeDefinitionId);
  }

  get availableFilterFields(): { stateLead: boolean; contactCycle: boolean; origin: boolean } {
    return {
      stateLead: this.viewMode !== 'stateLead',
      contactCycle: this.viewMode !== 'contactCycle',
      origin: this.viewMode !== 'origin'
    };
  }

  drop(event: CdkDragDrop<Contact[]>, targetColumn: KanbanColumn): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      this.updateContactField(event.container.data[event.currentIndex], targetColumn);
    }
  }

  private updateContactField(contact: Contact, targetColumn: KanbanColumn): void {
    if (!contact.id) return;

    const reload = () => this.loadPage(this.globalPage);

    if (this.viewMode === 'contactCycle') {
      if (targetColumn.id === 0) { reload(); return; }

      const cycle = this.contactCycles.find(c => c.id === targetColumn.id);

      if (cycle?.triggerAction === 'CREATE_DEAL' || cycle?.triggerAction === 'CREATE_LEAD') {
        this.triggerContact = contact;
        this.triggerTargetColumn = targetColumn;
        this.triggerType = cycle.triggerAction;
        this.openTriggerDrawer(contact, cycle.triggerAction);
        return;
      }

      this.contactoApi.changeContactCycle(contact.id, targetColumn.id).subscribe({
        next: () => reload(),
        error: (err) => {
          console.error('Error al cambiar ciclo:', err);
          this.confirmDialog.alert({ title: 'Error', message: 'Error al cambiar el ciclo. Por favor, recargue la página.', variant: 'danger' });
          reload();
        }
      });
      return;
    }

    const updatedContact: ContactRequest = {
      fullname: contact.fullname,
      lastname: contact.lastname,
      mail: contact.mail,
      phone: contact.phone,
      position: contact.position,
      businessId: contact.businessId,
      idContactState: contact.idContactState,
      idOrigin: contact.idOrigin,
      score: contact.score
    };

    switch (this.viewMode) {
      case 'stateLead': updatedContact.idContactState = targetColumn.id === 0 ? undefined : targetColumn.id; break;
      case 'origin':    updatedContact.idOrigin       = targetColumn.id === 0 ? undefined : targetColumn.id; break;
    }

    this.contactoApi.updateContact(contact.id, updatedContact).subscribe({
      next: () => reload(),
      error: (err) => {
        console.error('Error al actualizar contacto:', err);
        this.confirmDialog.alert({ title: 'Error', message: 'Error al actualizar el contacto. Por favor, recargue la página.', variant: 'danger' });
        reload();
      }
    });
  }

  getColumnIds(): string[] {
    return this.columns.map(col => `col-${col.id}`);
  }

  nuevoContacto(): void { this.router.navigate(['/contactos/crear']); }
  vistaLista(): void { this.router.navigate(['/contactos/lista']); }

  editarContacto(contact: Contact, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/contactos/editar', contact.id]);
  }

  /**
   * Inactiva (soft-delete) el contacto desde la tarjeta del Kanban.
   *
   * El Kanban solo carga contactos ACTIVOS (`loadPage` no manda `isActive`, y el backend
   * devuelve activos por defecto), por eso aquí NO se ofrece "Eliminar": el borrado real
   * exige que el contacto ya esté inactivo, así que ese botón solo podía abrir el aviso
   * "No se puede eliminar el contacto". La baja definitiva vive en la pestaña "Inactivos"
   * de la vista de lista.
   */
  async inactivarContacto(contact: Contact, event: Event): Promise<void> {
    event.stopPropagation();
    if (!contact.id) return;
    const nombre = `${contact.fullname ?? ''} ${contact.lastname ?? ''}`.trim();

    const ok = await this.confirmDialog.confirm({
      title: 'Inactivar contacto',
      message: `¿Está seguro de inactivar al contacto "${nombre}"? Una vez inactivo, el contacto no aparecerá en los procesos activos del sistema. Podrá reactivarlo o eliminarlo después desde la pestaña "Inactivos" de la lista.`,
      variant: 'warning',
      confirmText: 'Inactivar',
      cancelText: 'Cancelar'
    });
    if (!ok) return;

    this.contactoApi.archiveContact(contact.id).subscribe({
      next: () => {
        this.loadPage(this.globalPage);
        this.toast.success({ title: 'Contacto inactivado', message: `"${nombre}" ha sido inactivado correctamente.` });
      },
      error: (err) => {
        // 409 del backend: el contacto tiene negocios/tareas/tickets vigentes.
        this.confirmDialog.alert({
          title: 'No se puede inactivar el contacto',
          message: err.error?.message || 'Error al inactivar el contacto',
          variant: 'warning'
        });
      }
    });
  }

  getFullName(contact: Contact): string {
    return contact.lastname ? `${contact.fullname} ${contact.lastname}` : contact.fullname;
  }

  getContactStateName(id?: number): string {
    if (!id) return 'Sin estado';
    return this.contactStates.find(s => s.id === id)?.name || '—';
  }

  getCycleName(id?: number): string {
    if (!id) return 'Sin ciclo';
    return this.contactCycles.find(c => c.id === id)?.name || '—';
  }

  getOriginName(id?: number): string {
    if (!id) return 'Sin origen';
    return this.contactOrigins.find(o => o.id === id)?.name || '—';
  }

  get viewModeTitle(): string {
    switch (this.viewMode) {
      case 'stateLead':    return 'Por Estado Lead';
      case 'contactCycle': return 'Por Ciclo de Contacto';
      case 'origin':       return 'Por Origen';
    }
  }

  verDetalle(contacto: Contact): void {
    this.selectedContact = contacto;
    this.showDetailModal = true;
  }

  /**
   * Carga el contacto por id y abre su modal de detalle. Entrada de ?openContact=<id>:
   * se pide al backend en vez de buscarlo en las columnas para que funcione aunque
   * el contacto no esté en la página cargada del kanban.
   */
  private abrirDetallePorId(id: number): void {
    this.contactoApi
      .getContactById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contacto) => {
          this.selectedContact = contacto;
          this.showDetailModal = true;
        },
        error: () => {
          this.toast.error({
            title: 'No se pudo abrir el contacto',
            message: 'El contacto no existe o no tienes acceso.'
          });
        }
      });
  }

  cerrarModalDetalle(): void {
    this.showDetailModal = false;
    this.selectedContact = null;
    // Limpiar ?openContact para poder reabrir el mismo contacto desde el buscador
    // (navegar dos veces a la misma URL no vuelve a emitir el query param).
    if (this.route.snapshot.queryParamMap.has('openContact')) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { openContact: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
  }

  parseListOptions(listOptions?: string | null): string[] {
    if (!listOptions) return [];
    try { return JSON.parse(listOptions); } catch { return []; }
  }
}
